import React, {Component, ReactNode} from "react"
import PropTypes from "prop-types"
import {Col} from "react-bootstrap"
import {omit, filter, every, isString, isArray, map, find, xor, isEmpty} from "lodash"
import Select, {ValueType, OptionTypeBase, SelectComponentsConfig, Props as ReactSelectProps} from "react-select"
import Creatable from "react-select/creatable"
import Orderable from "react-select-draggable"

import {ValidationInput} from "@irati/utils/validation"
import newId from "@irati/utils/id"
import {InjectIntl, injectIntl, getResolvedMessage} from "@irati/utils/lang"


import Label from "./styled/Label"
import perkboxStyles from "./styles/selectStyles"
import SelectOptionComponent from "./SelectOption"
import SelectValueComponent from "./SelectValue"
import SelectPillComponent from "./SelectPill"
import InfoTooltip from "./utils/InfoTooltip"
import HintText from "@irati/ui/src/HintText"
import ErrorText from "@irati/ui/src/ErrorText"
import {FormGroup} from "@irati/ui/src/Form"

export interface SelectOption extends OptionTypeBase {
  label: string,
  value: string
}

export type SelectValueType = ValueType<SelectOption>

interface OptionalProps {
  label?: string,
  value?: SelectValueType,
  inputValue?: string,
  hint?: ReactNode,
  placeholder?: string,
  className?: string,
  id?: string,
  labelKey?: string,
  valueKey?: string,
  hideLabel?: boolean,
  autoFocus?: boolean,
  promptTextCreator?: (label: string) => string,
  onInputChange?: ReactSelectProps["onInputChange"],
  onBlur?: () => ReactSelectProps["onBlur"],
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void,
  components?: SelectComponentsConfig<SelectOption>,
  getOptionLabel?: (option: SelectOption) => string,
  getOptionValue?: (option: SelectOption) => string,
  controlShouldRenderValue?: boolean,
  isMulti?: boolean,
  isDisabled?: boolean,
  isCreatable?: boolean,
  isOrderable?: boolean,
  isClearable?: boolean,
  isSearchable?: boolean,
  menuIsOpen?: boolean,
  flush?: boolean,
  tooltip?: string
}

export type SelectOnChange = (value: string | SelectOption[] | null, option: any) => void

export interface SelectProps extends OptionalProps {
  name: string,
  options: SelectOption[],
  onChange: SelectOnChange
}

interface Props extends SelectProps, InjectIntl {}

interface State {
  errorMsg?: string,
  blurred: boolean,
  value?: SelectValueType
}

export class FormItemSelect extends Component<Props, State> {
  validation: ValidationInput

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  static defaultProps: Partial<SelectProps> = {
    hideLabel: false,
    labelKey: "label",
    valueKey: "value",
    promptTextCreator: (label: string) => `Create option ${label}`,
    isCreatable: false,
    isDisabled: false
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const {value: propsValue} = props
    const {value: stateValue} = state

    if (
      (isString(propsValue) && propsValue !== stateValue) ||
      isEmpty(stateValue) ||
      (isArray(propsValue) && (isArray(stateValue) && xor(propsValue, stateValue).length > 0))
    ) {
      return {
        value: propsValue
      }
    }
    return null
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      errorMsg: undefined,
      blurred: false
    }

    this.validation = new ValidationInput(this)
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    this.validation.update(prevProps, prevState)
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  handleBlur = () => {
    this.validation.update(this.props, this.state)
    if (typeof this.props.onBlur === "function") {
      this.props.onBlur()
    }
  }

  handleChange = (option: any) => {
    const {isMulti, onChange} = this.props
    let value = isMulti ? [] : null

    if (option) {
      if (isMulti) {
        value = option.map((multiOption: SelectOption) => {
          return {
            ...multiOption,
            label: this.getOptionLabel(multiOption),
            value: this.getOptionValue(multiOption)
          }
        })
      } else {
        value = this.getOptionValue(option)
      }
    }
    this.setState({value})
    onChange(value, option)
  }

  mapValueToOptions = () => {
    const {options} = this.props
    const {value} = this.state

    if (isArray(value) && isArray(options) && options.length > 0) {
      if (every(value, isString)) {
        return map(value, (selectedValue) => {
          return (
            find(options, ({value: optionValue}) => {
              return optionValue === selectedValue
            }) || {
              // created new entry
              label: selectedValue,
              value: selectedValue
            }
          )
        })
      }
      return value
    }
    return undefined
  }

  setValue = () => {
    const {options, isMulti} = this.props

    if (isMulti) {
      const {value} = this.state
      return value && every(value, isString) ? this.mapValueToOptions() : value
    }
    const {value} = this.props
    return (typeof value === "string" || typeof value === "boolean") && options
      ? filter(options, ({value: optionValue}) => optionValue === value)
      : value
  }

  private getOptionLabel = (option: SelectOption) => {
    const {labelKey = "label", getOptionLabel = () => option[labelKey]} = this.props
    return getOptionLabel(option)
  }

  private getOptionValue = (option: SelectOption) => {
    const {valueKey = "value", getOptionValue = () => option[valueKey]} = this.props
    return getOptionValue(option)
  }

  render() {
    const {
      name,
      label,
      className,
      hint,
      id,
      promptTextCreator,
      hideLabel,
      onInputChange,
      components,
      isCreatable,
      isDisabled,
      isOrderable,
      flush,
      selectRef,
      tooltip,
      styles,
      menuIsOpen
    } = this.props
    const {errorMsg} = this.state

    const selectProps = omit(this.props, ["className", "label", "validation", "formatMessage", "translate"])

    const props = {
      style: {border: "1px solid #979797"},
      ...selectProps,
      id: newId("select", id || name),
      name: newId("select", id || name),
      value: this.setValue(),
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      onInputChange,
      getOptionLabel: this.getOptionLabel,
      getOptionValue: this.getOptionValue,
      isDisabled,
      menuIsOpen
    }

    const selectStyles = styles || perkboxStyles

    return (
      <FormGroup className={className} controlId={name} validationState={errorMsg ? "error" : undefined} flush={flush}>
        <Col hidden={hideLabel} xs={tooltip ? 10 : 12}>
          {label && (
            <Label id={newId("label", id || name)} error={errorMsg} htmlFor={name} flush>
              {getResolvedMessage(this, "label")}
            </Label>
          )}
        </Col>
        {tooltip && (
          <Col xs={2}>
            <InfoTooltip tooltip={tooltip} />
          </Col>
        )}
        <Col xs={12}>
          {isCreatable && (
            <Creatable
              {...props}
              placeholder={getResolvedMessage(this, "placeholder")}
              promptTextCreator={promptTextCreator}
              components={{
                Option: SelectOptionComponent,
                SingleValue: SelectValueComponent,
                MultiValueLabel: SelectPillComponent,
                IndicatorSeparator: () => null,
                ...components
              }}
              styles={selectStyles} />
          )}
          {!isCreatable &&
            (isOrderable ? (
              <Orderable {...props} styles={selectStyles} />
            ) : (
              <Select
                {...props}
                ref={selectRef}
                placeholder={getResolvedMessage(this, "placeholder")}
                components={{
                  Option: SelectOptionComponent,
                  SingleValue: SelectValueComponent,
                  MultiValueLabel: SelectPillComponent,
                  IndicatorSeparator: () => null,
                  ...components
                }}
                styles={selectStyles} />
            ))}

          {errorMsg && (
            <div>
              <ErrorText small>{getResolvedMessage(this, "errorMsg", this.state)}</ErrorText>
            </div>
          )}
          {hint && (
            <div>
              <HintText small>{hint}</HintText>
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(FormItemSelect)
